import { useState, useRef, useEffect } from "react";
import {
  ChevronDownIcon,
  DeleteIcon,
  RepeatIcon,
} from "@chakra-ui/icons";
import {
  HStack,
  IconButton,
  Badge,
  Button,
  Collapse,
  useDisclosure,
  AlertDialog,
  AlertDialogOverlay,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogBody,
  AlertDialogFooter,
  Text,
  Spinner,
  Box,
  Flex,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Switch,
  Tag,
  Wrap,
  WrapItem,
  VStack,
} from "@chakra-ui/react";
import { FiTerminal, FiUsers } from "react-icons/fi";
import { Icon } from "@chakra-ui/react";
import { epochConverterWithTimeZone } from "utils/dateUtils";
import { Header } from "build-ts/metadata";
import {
  JobRunStatus,
  ScheduledJobRunProto,
} from "build-ts/schedule";
import {
  getScheduledJobRun,
  setScheduledJobRunVisibility
} from "services/ScheduleServices";
import showToast from "hooks/useCustomToast";
import AnalysisScenarioInsightsCard from "./AnalysisScenarioInsightsCard";
import AnalysisLogsTerminal from "./AnalysisLogsTerminal";
import { useUser } from "context/UserContext";
import { updateChatSessionHeaders } from "services/AgentChatService";

interface AnalysisRunCardProps {
  jobRun: Header;
  onDelete: (runId: string) => void;
  expandAllOutputs?: boolean;
  hideAnalystControls?: boolean;
}

const AnalysisRunCard: React.FC<AnalysisRunCardProps> = ({
  jobRun,
  onDelete,
  expandAllOutputs = false,
  hideAnalystControls = false,
}) => {
  const [runDetails, setRunDetails] = useState<ScheduledJobRunProto | null>(
    null
  );
  const [isLoading, setIsLoading] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isTogglingVisibility, setIsTogglingVisibility] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [showSystemLogs, setShowSystemLogs] = useState(false);
  const deleteDialog = useDisclosure();
  const cancelRef = useRef<HTMLButtonElement>(null);
  const { defaultTimeZone } = useUser();

  const extractSessionIds = (analystRun: any): string[] => {
    const sessionIds: string[] = [];
    if (analystRun?.scenario_chat_sessions) {
      analystRun.scenario_chat_sessions.forEach((item: any) => {
        if (item?.session_id) {
          sessionIds.push(item.session_id);
        }
      });
    }
    if (analystRun?.synthesis_session_id) {
      sessionIds.push(analystRun.synthesis_session_id);
    }
    if (analystRun?.contextualization_session_id) {
      sessionIds.push(analystRun.contextualization_session_id);
    }
    if (analystRun?.slack_chat_session_id) {
      sessionIds.push(analystRun.slack_chat_session_id);
    }
    return sessionIds;
  };

  const getStatusInfo = (status: JobRunStatus) => {
    switch (status) {
      case JobRunStatus.PENDING:
        return { color: "gray", label: "Pending" };
      case JobRunStatus.IN_PROGRESS:
        return { color: "blue", label: "In Progress" };
      case JobRunStatus.SUCCESS:
        return { color: "green", label: "Success" };
      case JobRunStatus.FAILURE:
        return { color: "red", label: "Failed" };
      case JobRunStatus.PARTIAL_SUCCESS:
        return { color: "orange", label: "Partial Success" };
      default:
        return { color: "gray", label: "Unknown" };
    }
  };

  useEffect(() => {
    const fetchRunDetails = async () => {
      setIsLoading(true);
      const details = await getScheduledJobRun(jobRun?.id || "");
      if (details) {
        setRunDetails(details);
        setIsVisible(details?.visibility || false);
      } else {
        setRunDetails(null);
      }
      setIsLoading(false);
    };
    fetchRunDetails();
  }, [jobRun?.id]);

  const handleRefreshRun = async () => {
    if (!jobRun.id) return;

    setIsRefreshing(true);
    const details = await getScheduledJobRun(jobRun.id);
    if (details) {
      setRunDetails(details);
      setIsVisible(details?.visibility || false);
      showToast("Run status refreshed successfully", "", "success");
    } else {
      showToast("Failed to refresh run status", "", "error");
    }
    setIsRefreshing(false);
  };

  const handleVisibilityToggle = async (newVisibility: boolean) => {
    if (!jobRun.id) return;

    setIsTogglingVisibility(true);
    const newtonResponse = await setScheduledJobRunVisibility(jobRun.id, newVisibility);
    if (runDetails?.scenario_run) {
      const sessionIds = extractSessionIds(runDetails.scenario_run);
      if (sessionIds.length > 0) {
        const darwinResponse = await updateChatSessionHeaders(
          sessionIds,
          undefined,
          undefined,
          !newVisibility
        );
        if (newtonResponse && darwinResponse) {
          setIsVisible(newVisibility);
          showToast(
            `Job run ${newVisibility ? "shown" : "hidden"} (${sessionIds.length} chat session${sessionIds.length > 1 ? "s" : ""} updated)`,
            "",
            "success"
          );
        } else if (newtonResponse && !darwinResponse) {
          setIsVisible(newVisibility);
          showToast(
            "Job run visibility updated, but some chat sessions may not be updated",
            "",
            "warning"
          );
        } else {
          showToast("Failed to update visibility", "", "error");
        }
      } else {
        if (newtonResponse) {
          setIsVisible(newVisibility);
          showToast(
            `Job run ${newVisibility ? "shown" : "hidden"} successfully`,
            "",
            "success"
          );
        } else {
          showToast("Failed to update job run visibility", "", "error");
        }
      }
    } else {
      if (newtonResponse) {
        setIsVisible(newVisibility);
        showToast(
          `Job run ${newVisibility ? "shown" : "hidden"} successfully`,
          "",
          "success"
        );
      } else {
        showToast("Failed to update job run visibility", "", "error");
      }
    }
    setIsTogglingVisibility(false);
  };

  const handleDelete = () => {
    if (jobRun.id) {
      onDelete(jobRun.id);
    }
    deleteDialog.onClose();
  };

  const handleHeaderClick = () => {
    const firstSessionId = runDetails?.scenario_run?.analyst_chat_session_id;
    if (firstSessionId) {
      window.open(`/chat/${firstSessionId}`, "_blank");
    }
  };

  const statusInfo = runDetails
    ? getStatusInfo(runDetails?.status || JobRunStatus.PENDING)
    : null;

  const formatRunDate = () => {
    if (!jobRun?.created_date_ms) return "N/A";
    return epochConverterWithTimeZone(jobRun.created_date_ms, "d-MMM-yyyy, HH:mm", defaultTimeZone) + " " + defaultTimeZone.toUpperCase();
  };

  return (
    <>
      <Flex
        key={jobRun.id}
        p={3}
        align="flex-start"
        borderRadius="lg"
        border="1px solid"
        borderColor="gray.300"
        bg="white"
        cursor="pointer"
        transition="all 0.15s ease"
        _hover={{ borderColor: "gray.500", bg: "gray.50" }}
        role="group"
        tabIndex={0}
        direction="column"
      >
        {/* Header row: Title + Status + Menu */}
        <Flex align="center" w="100%" flex={1}>
          <Box
            flex="1"
            minW={0}
            onClick={handleHeaderClick}
          >
            <Text
              fontSize="md"
              fontWeight="600"
              color="fg.default"
              noOfLines={1}
              align="left"
              m={0}
            >
              {formatRunDate()}
            </Text>
          </Box>

          <HStack spacing={2} align="center">
            {runDetails?.status && statusInfo && (
              <Badge colorScheme={statusInfo.color} fontSize="xs">
                {statusInfo.label}
              </Badge>
            )}
            <Menu placement="bottom-end">
              <MenuButton
                as={IconButton}
                icon={<ChevronDownIcon boxSize={5} />}
                size="xs"
                variant="ghost"
                color="purple.700"
                _hover={{ bg: "purple.700", color: "white" }}
                aria-label="Run options"
                onClick={(e) => e.stopPropagation()}
              />
              <MenuList minW="180px" boxShadow="lg" zIndex={1001}>
                {!hideAnalystControls && (
                  <MenuItem
                    closeOnSelect={false}
                    onClick={(e) => e.stopPropagation()}
                    isDisabled={isTogglingVisibility}
                    fontSize="sm"
                  >
                    <Flex align="center" gap={3} w="100%">
                      <Box flexShrink={0} display="flex" alignItems="center">
                        <Switch
                          size="sm"
                          colorScheme="purple"
                          isChecked={isVisible}
                          onChange={(e) => {
                            e.stopPropagation();
                            handleVisibilityToggle(e.target.checked);
                          }}
                          onClick={(e) => e.stopPropagation()}
                          isDisabled={isTogglingVisibility}
                          mb={0}
                        />
                      </Box>
                      <Text as="span" fontSize="sm" lineHeight="1" mb={0}>
                        {isVisible ? "Analyst can see" : "Analyst cannot see"}
                      </Text>
                    </Flex>
                  </MenuItem>
                )}
                <MenuItem
                  icon={isRefreshing ? <Spinner size="xs" /> : <Icon as={RepeatIcon} boxSize={4} />}
                  fontSize="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRefreshRun();
                  }}
                  isDisabled={isRefreshing}
                >
                  Refresh
                </MenuItem>
                {runDetails?.run_logs && runDetails.run_logs.length > 0 && (
                  <MenuItem
                    icon={<Icon as={FiTerminal} boxSize={4} />}
                    fontSize="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowSystemLogs(prev => !prev);
                    }}
                  >
                    System Logs
                  </MenuItem>
                )}
                <MenuItem
                  icon={<Icon as={DeleteIcon} boxSize={4} />}
                  fontSize="sm"
                  color="red.500"
                  onClick={(e) => {
                    e.stopPropagation();
                    deleteDialog.onOpen();
                  }}
                >
                  Delete
                </MenuItem>
              </MenuList>
            </Menu>
          </HStack>
        </Flex>

        {/* Content: Scenario insights (always visible when loaded) */}
        {runDetails && (
          <Box w="100%" mt={1} pl={0}>
            {isLoading ? (
              <HStack justify="center" py={4}>
                <Spinner size="sm" />
                <Text fontSize="sm">Loading run details...</Text>
              </HStack>
            ) : runDetails?.scenario_run ? (
              <AnalysisScenarioInsightsCard
                scenarioRun={runDetails.scenario_run}
                expandAllOutputs={expandAllOutputs}
              />
            ) : runDetails?.column_sync_run ? (
              <VStack align="stretch" spacing={2} pt={1}>
                <HStack spacing={2} flexWrap="wrap">
                  <Badge colorScheme="green" variant="subtle" fontSize="xs">
                    {runDetails.column_sync_run.populated_analyst_headers.length} synced
                  </Badge>
                  {runDetails.column_sync_run.skipped_analyst_headers.length > 0 && (
                    <Badge colorScheme="red" variant="subtle" fontSize="xs">
                      {runDetails.column_sync_run.skipped_analyst_headers.length} skipped
                    </Badge>
                  )}
                </HStack>
                {runDetails.column_sync_run.populated_analyst_headers.length > 0 && (
                  <Box>
                    <HStack spacing={1} mb={1}>
                      <Icon as={FiUsers} boxSize={3} color="gray.400" />
                      <Text fontSize="xs" color="gray.500" m={0} fontWeight="600">Synced analysts</Text>
                    </HStack>
                    <Wrap spacing={1}>
                      {runDetails.column_sync_run.populated_analyst_headers.map((h) => (
                        <WrapItem key={h.id}>
                          <Tag size="sm" colorScheme="purple" variant="subtle" borderRadius="md">
                            {h.name || h.id}
                          </Tag>
                        </WrapItem>
                      ))}
                    </Wrap>
                  </Box>
                )}
                {runDetails.column_sync_run.skipped_analyst_headers.length > 0 && (
                  <Box>
                    <HStack spacing={1} mb={1}>
                      <Icon as={FiUsers} boxSize={3} color="red.400" />
                      <Text fontSize="xs" color="red.400" m={0} fontWeight="600">Skipped analysts</Text>
                    </HStack>
                    <Wrap spacing={1}>
                      {runDetails.column_sync_run.skipped_analyst_headers.map((h) => (
                        <WrapItem key={h.id}>
                          <Tag size="sm" colorScheme="red" variant="subtle" borderRadius="md">
                            {h.name || h.id}
                          </Tag>
                        </WrapItem>
                      ))}
                    </Wrap>
                  </Box>
                )}
              </VStack>
            ) : null}

            {/* System Logs - terminal view shown directly when toggled from menu */}
            <Collapse in={showSystemLogs}>
              {runDetails?.run_logs && runDetails.run_logs.length > 0 && (
                <Box mt={3}>
                  <AnalysisLogsTerminal logs={runDetails.run_logs} />
                </Box>
              )}
            </Collapse>
          </Box>
        )}
      </Flex>

      <AlertDialog
        isOpen={deleteDialog.isOpen}
        leastDestructiveRef={cancelRef}
        onClose={deleteDialog.onClose}
        isCentered
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Delete Analysis Run
            </AlertDialogHeader>
            <AlertDialogBody>
              Are you sure you want to delete this analysis run? This action cannot
              be undone.
              <strong>Run:</strong> {jobRun?.name || "Unknown Run"}
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={deleteDialog.onClose}>
                Cancel
              </Button>
              <Button colorScheme="red" onClick={handleDelete} ml={3}>
                Delete
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </>
  );
};

export default AnalysisRunCard;
